import { useContext } from 'react'

import {
  Button,
  Columns,
  LoadingModule,
  SaveFormModule,
} from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import {
  isAgencyUser, isAgent, isBrandUser, isProviderUser,
} from '@functions/user'

import useCurrentUser from '@hooks/useCurrentUser'
import useUser, { useUserForm } from '@hooks/useUser'

import ApiKeys from '@components/ApiKeys'
import CustomShortcodes from '@components/CustomShortcodes'
import ScopedConditionalContent from '@components/ScopedConditionalContent'
import SupportUsersList from '@components/SupportUsersList'

import Account from '../Account'
import Advanced from '../Advanced'
import Details from '../Details'
import PageLayout from '../../../../components/PageLayout'
import UserAssociation from '../UserAssociation'
import UserImages from '../UserImages'
import UserSettings from '../UserSettings'
import UserTools from '../../../../components/UserTools'
import UserDeletedBanner from '../UserDeletedBanner'

interface PageContentProps {
  userId: number,
}

const PageContent = (props: PageContentProps) => {
  const { userId } = props

  const userPayload = useUser({ id: userId })

  const {
    agency,
    callbacks: {
      manageUserSupportTeam,
      updateUser: updateFn,
    },
    isSelf,
    loading,
    role,
    // @ts-expect-error TS2339
    role: { title },
    supportUserIds,
    updating,
    user,
    user: {
      association_id,
      association_type,
    },
  } = userPayload

  const agencyUser = isAgencyUser(title)
  const brandUser = isBrandUser(title)
  const userIsAgent = isAgent(title)
  const providerUser = isProviderUser(title)

  const userForm = useUserForm(user)
  const { entityState, saveEnabled } = userForm

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      updateUser,
    },
  } = pageContext

  const updateUserPayload = {
    callbacks: {
      action: updateFn,
    },
    entityParams: entityState,
    toastText: 'User Updated',
  }

  const currentUserPayload = useCurrentUser()
  const { isAdmin, isBrandUser: currentUserIsBrandUser } = currentUserPayload

  return (
    // @ts-expect-error TS2322
    <PageLayout activeTabBarItemKey="details" entityPayload={userPayload}>
      <Columns.Main>
        {/*
         // @ts-expect-error TS2322 */}
        <LoadingModule loading={loading} times={3} />

        <UserDeletedBanner userPayload={userPayload} />

        {!loading && (
          <>
            <Details userForm={userForm} />

            {(!currentUserIsBrandUser || isSelf) && (
              // @ts-expect-error TS2739
              <Account role={role} userForm={userForm} />
            )}

            <ScopedConditionalContent permittedRoleTitles={['Admin', 'Support', 'Provider Director']}>
              {/*
               // @ts-expect-error TS2740 */}
              <Advanced agency={agency} userForm={userForm} />
            </ScopedConditionalContent>

            {/*
             // @ts-expect-error TS2741 */}
            <ApiKeys
              filters={{
                default_user_id: userId,
                owner_id: association_id,
                owner_type: association_type,
              }}
              hideFilters
            />

            <ScopedConditionalContent permittedRoleTitles={['Admin', 'Support']}>
              {/*
               // @ts-expect-error TS2339 */}
              {isAgent(role?.title) && <CustomShortcodes ownerId={userId} ownerType="User" />}
            </ScopedConditionalContent>

            <SaveFormModule>
              <Button
                buttonStyle="primaryCreate"
                disabled={!saveEnabled}
                loading={updating}
                onClick={() => updateUser(updateUserPayload)}
                size="medium"
              >
                Save Details
              </Button>
            </SaveFormModule>
          </>
        )}
      </Columns.Main>

      <Columns.Sidebar>
        <ScopedConditionalContent
          permittedRoleTitles={['Admin', 'Support', 'Provider Director']}
        >
          <UserTools userPayload={userPayload} />

          <UserImages userId={userId} />

          {userIsAgent && <SupportUsersList onClick={manageUserSupportTeam} userIds={supportUserIds} /> }
        </ScopedConditionalContent>

        {(agencyUser || brandUser || (providerUser && isAdmin)) && (
          <UserAssociation
            currentUserPayload={currentUserPayload}
            updateUser={updateUser}
            userForm={userForm}
            userPayload={userPayload}
          />
        )}

        <ScopedConditionalContent
          permittedRoleTitles={['Admin', 'Support', 'Provider Director']}
        >
          <UserSettings userForm={userForm} userPayload={userPayload} />
        </ScopedConditionalContent>
      </Columns.Sidebar>
    </PageLayout>
  )
}

export default PageContent
